import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.types';

export type MenuItemRow = Database['public']['Tables']['menu_items']['Row'];

const HEADER_SLUG = 'primary';

export type LinkType = 'page' | 'external' | 'anchor';

/** Deriva o tipo de link de um item a partir dos campos persistidos. */
export function linkTypeOf(item: Pick<MenuItemRow, 'page_id' | 'url'>): LinkType {
  if (item.page_id) return 'page';
  if (item.url?.startsWith('#')) return 'anchor';
  return 'external';
}

/** Garante (cria se necessário) e retorna o menu de header do site ativo. */
export function useHeaderMenu(siteId: string | null) {
  return useQuery({
    queryKey: ['header-menu', siteId],
    queryFn: async () => {
      if (!siteId) return null;
      const { data: existing, error } = await supabase
        .from('menus')
        .select('id, slug, name')
        .eq('site_id', siteId)
        .eq('slug', HEADER_SLUG)
        .maybeSingle();
      if (error) throw error;
      if (existing) return existing;

      const { data: created, error: createError } = await supabase
        .from('menus')
        .insert({ site_id: siteId, slug: HEADER_SLUG, name: 'Menu principal', location: 'header' })
        .select('id, slug, name')
        .single();
      if (createError) throw createError;
      return created;
    },
    enabled: !!siteId,
  });
}

/** Itens do menu (ordenados) para edição no admin. */
export function useMenuItems(menuId: string | null) {
  return useQuery({
    queryKey: ['menu-items', menuId],
    queryFn: async () => {
      if (!menuId) return [];
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .eq('menu_id', menuId)
        .order('order_index', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!menuId,
  });
}

export function useMenuMutations(menuId: string | null) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['menu-items', menuId] });

  const createItem = useMutation({
    mutationFn: async (input: {
      label: string;
      page_id: string | null;
      url: string | null;
      target: string;
      parent_id: string | null;
      order_index: number;
    }) => {
      if (!menuId) throw new Error('Menu não inicializado.');
      const { error } = await supabase.from('menu_items').insert({ menu_id: menuId, ...input });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const updateItem = useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<MenuItemRow>) => {
      const { error } = await supabase.from('menu_items').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deleteItem = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('menu_items').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  /** Persiste a nova ordem (order_index sequencial) de uma lista de ids. */
  const reorder = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await Promise.all(
        orderedIds.map((id, index) => supabase.from('menu_items').update({ order_index: index }).eq('id', id)),
      );
    },
    onSuccess: invalidate,
  });

  return { createItem, updateItem, deleteItem, reorder };
}

// ─────────────────────────────────────────────────────────────────────────
// Leitura pública (site) — menu em árvore de 2 níveis, com slug da página.
// ─────────────────────────────────────────────────────────────────────────
export interface PublicMenuItem {
  id: string;
  label: string;
  page_id: string | null;
  page_slug: string | null;
  url: string | null;
  target: string | null;
  children: PublicMenuItem[];
}

export function usePublicMenu(siteId: string | null | undefined) {
  return useQuery({
    queryKey: ['public-menu', siteId],
    queryFn: async (): Promise<PublicMenuItem[]> => {
      if (!siteId) return [];
      const { data: menu, error: menuError } = await supabase
        .from('menus')
        .select('id')
        .eq('site_id', siteId)
        .eq('slug', HEADER_SLUG)
        .maybeSingle();
      if (menuError) throw menuError;
      if (!menu) return [];

      const { data: items, error } = await supabase
        .from('menu_items')
        .select('id, label, page_id, url, target, parent_id, order_index, page:posts(slug)')
        .eq('menu_id', menu.id)
        .order('order_index', { ascending: true });
      if (error) throw error;

      const rows = items ?? [];
      const toNode = (r: (typeof rows)[number]): PublicMenuItem => ({
        id: r.id,
        label: r.label,
        page_id: r.page_id,
        page_slug: r.page?.slug ?? null,
        url: r.url,
        target: r.target,
        children: [],
      });

      const byId = new Map<string, PublicMenuItem>();
      rows.forEach((r) => byId.set(r.id, toNode(r)));
      const roots: PublicMenuItem[] = [];
      rows.forEach((r) => {
        const node = byId.get(r.id)!;
        if (r.parent_id && byId.has(r.parent_id)) byId.get(r.parent_id)!.children.push(node);
        else roots.push(node);
      });
      return roots;
    },
    enabled: !!siteId,
  });
}
